<!DOCTYPE HTML>
<template>
<div class="onboard-card"
     :class="{'active':hasStarted}"
     v-if="source">

  <b-card class="shadow">
    <div class="persona">

      <div class="illustrations"
           v-if="!enlarged">
        <img v-if="type=='client'"
             class="illustration"
             src="@/assets/Client.png" />
        <img v-if="type=='provider'"
             class="illustration"
             src="@/assets/Provider.png" />
      </div>

      <Progress v-if="enlarged"
                :progress="progress"
                @exit="close()" />


      <b-alert variant="danger"
               :show="error">{{error}}</b-alert>

      <Question class="question"
                :questions="requiredQuestions"
                :answers="answers"
                :questionCount="position"
                @started="started()"
                @update-position="updatePosition"
                @finish="finish()">

        <slot>
          <div v-if="type=='client'">
            <b-button variant="outline-success"
                      to="/help">Help me decide</b-button>
          </div>
        </slot>
      </Question>
    </div>
  </b-card>
</div>
</template>

<script>
// Firebase
const firebase = require( '@/firebaseConfig.js' )
// Components
import Question from '@/components/Question.vue'
import Progress from '@/components/Progress.vue'
// Mixins
import { Actions } from '@/mixins/Mixins.js'
export default {
  name: 'OnboardProviders',
  components: {
    Question,
    Progress,
  },
  mixins: [ Actions ],
  props: {
    enlarged: Boolean,
    user: Object,
    type: String,
  },
  firestore: {
    clientQuestions: firebase.db.collection( 'clientOnboard' ).orderBy( 'order' ),
    providerQuestions: firebase.db.collection( 'providerOnboard' ).orderBy( 'order' ),
  },
  data() {
    return {
      'clientQuestions': {},
      'providerQuestions': {},
      'hasStarted': false,
      'position': 0,
      'answers': {},
      'error': null,
    }
  },
  computed: {
    source() {
      if ( this.type == 'client' ) {
        return this.clientQuestions
      }
      return this.providerQuestions
    },
    allQuestions() {
      /*
      These are all the questions that exist in firebase for the provider onboarding, but not
      all of them will be relevant for our user.
      */
      return this.source
    },
    requiredQuestions() {
      /*
      Only send questions that are 'required'. All questions have a 'required' boolean.
      Some questions have a 'requires' field that points to a skill or industry ID. For the questions
      with a 'requires', we check against the 'broad_skills' or 'companyTypes' answer and dynamically set
      whether the question is required (and therefore shown).
      */
      return this.allQuestions.filter( question => {
        if ( question.requires && this.type == 'provider' ) {
          let isSelected = this.broadSkills.find( skill => ( 'skills/' + skill.id ) == question.requires )
          question.required = isSelected
        }
        if ( question.requires && this.type == 'client') {
          let isSelected = this.industries.find( industry => ( 'companyTypes/' + industry.id ) == question.requires )
          question.required = isSelected
        }
        /*
        if ( this.type == 'client' && this.user ) {
          return question.id !== 'contact_info'
        }
        */
        return question.required
      } )
    },
    broadSkills() {
      /*
      These are the answers to a specific question ('broad_skills') that are used to figure out
      if we need to show follow up questions later in the questionnaire.
      */
      return this.answers.broad_skills ? this.answers.broad_skills : []
    },
    industries() {
      /*
      These are the answers to a specific question ('industries') that are used to figure out
      if we need to show follow up questions later in the questionnaire.
      */
      return this.answers.industries ? this.answers.industries : []
    },
    progress() {
      return {
        'value': this.position,
        'max': this.requiredQuestions.length
      }
    },
  },
  methods: {
    started() {
      this.hasStarted = true
      this.$emit( 'started', this.type )
    },
    close() {
      this.hasStarted = false
      this.position = 0
      this.$emit( 'started', false )
      this.$emit( 'close' )

    },
    updatePosition( p ) {
      this.position = p
    },
    finish() {

      if ( this.type == 'client' ) {

        let tempProjectData = {
          'goals': this.answers.broad_goals.map( goal => goal.ref ),
          'skills': this.answers.skills.map( skill => skill.ref ),
          'preferences': this.answers.preferences.map( pref => pref.ref ),
          'budget': this.answers.budget[ 0 ],
          'name': this.answers.description[ 0 ],
          'description': this.answers.description[ 1 ],
          'timeline': this.answers.due_date[ 0 ],
        }

        let tempUserData = {
          'type': 'client',
          'email': this.answers.contact_info[ 0 ],
          'company': {
            'name': this.answers.contact_info[ 3 ]
          },
          'name': {
            'first': this.answers.contact_info[ 1 ],
            'last': this.answers.contact_info[ 2 ],
          },
          'verified': false,
        }

        if ( this.user ) {
          this.createProject( tempProjectData )
            .then( () => {
              this.close()
            } )
            .catch( error => {
              this.error = error
            } )

        } else {

          let email = this.answers.contact_info[ 0 ]
          let password = this.answers.contact_info[ 4 ]

          this.createAccount( email, password, tempUserData )
            .then( () => {
              this.createProject( tempProjectData )
              this.$router.push( '/dashboard' ).catch( () => {} )
            } )
            .catch( error => {
              this.error = error
            } )

        }
      }

      if ( this.type == 'provider' ) {

        // Get all the unique chosen skills
        let skillCategories = [ 'more_idm', 'more_operations', 'more_policy', 'more_cloud', 'more_application' ]
        let skills = skillCategories.flatMap( category => {
          let skillCategory = this.answers[ category ]
          if ( skillCategory ) {
            return skillCategory.map( skill => skill.ref )
          }
        } ).filter( skills => skills != null )


        let preferences = this.answers.preferences.map( pref => pref.ref )
        let goals = this.answers.goals.map( goal => goal.ref )

        // Create a user object
        let tempUserData = {
          'type': 'provider',
          'skills': skills,
          'preferences': preferences,
          'goals': goals,
          'email': this.answers.contact_info[ 0 ],
          'name': {
            'first': this.answers.contact_info[ 1 ],
            'last': this.answers.contact_info[ 2 ],
          },
          'experience': this.answers.experience,
          'rate': {
            'min': this.answers.rate[ 0 ][ 0 ],
            'max': this.answers.rate[ 0 ][ 1 ]
          },
          'verified': false
        }

        let email = this.answers.contact_info[ 0 ]
        let password = this.answers.contact_info[ 3 ]

        this.createAccount( email, password, tempUserData )
          .then( () => {
            this.$router.push( '/dashboard' ).catch( () => {} )
          } )
          .catch( error => {
            this.error = error
          } )

      }
    }
  }
}
</script>


<style scoped lang="scss">
@import "@/styles/overrides.scss";

.onboard-card {
    display: grid;
    color: $primary;

    &.active {
        .persona {
            padding: 2rem;

            .question {
                padding: 2rem;
            }
        }
    }

    .persona {
        display: grid;
        padding: 2rem;
        grid-template-columns: 1fr;
        grid-template-rows: auto auto;


        .illustrations {
            justify-self: center;

            .illustration {
                height: 18rem;
            }
        }

        .question {
            padding: 1rem;
        }
    }
}
</style>
